import { Button, ButtonProps, Dropdown, Menu } from "antd";
import React, { FC, useCallback } from "react";
import { useCopyToClipboardAndShowToast } from "@dewo/app/util/hooks";
import { usePermission } from "@dewo/app/contexts/PermissionsContext";
import { useOrganizationDetails } from "../organization/hooks";
import { RulePermission } from "@dewo/app/graphql/types";
import { useCreateInvite } from "./hooks";

interface Props extends ButtonProps {
  organizationId: string;
}

export const OrganizationInviteButton: FC<Props> = ({
  organizationId,
  children,
  ...buttonProps
}) => {
  const { organization } = useOrganizationDetails(organizationId);
  const canInvite = usePermission("create", "Role");

  const copyToClipboardAndShowToast =
    useCopyToClipboardAndShowToast("Invite link copied");
  const createInvite = useCreateInvite();
  const inviteOrganizationAdmin = useCallback(async () => {
    const inviteLink = await createInvite({
      organizationId: organization!.id,
      permission: RulePermission.MANAGE_ORGANIZATION,
    });
    copyToClipboardAndShowToast(inviteLink);
  }, [createInvite, copyToClipboardAndShowToast, organization]);
  const inviteToTokenGatedWorkspaces = useCallback(async () => {
    copyToClipboardAndShowToast(organization!.permalink);
  }, [copyToClipboardAndShowToast, organization]);

  if (!organization) return null;

  const hasTokenGatedWorkspaces = !!organization.workspaceTokenGates.length;
  if (canInvite && !hasTokenGatedWorkspaces) {
    return (
      <Button onClick={inviteOrganizationAdmin} {...buttonProps}>
        {children ?? "Invite Admins"}
      </Button>
    );
  }

  if (!canInvite && hasTokenGatedWorkspaces) {
    return (
      <Button onClick={inviteToTokenGatedWorkspaces} {...buttonProps}>
        {children ?? "Invite"}
      </Button>
    );
  }

  if (canInvite && hasTokenGatedWorkspaces) {
    return (
      <Dropdown
        placement="topCenter"
        trigger={["click"]}
        overlay={
          <Menu>
            {canInvite && (
              <Menu.Item key="admin" onClick={inviteOrganizationAdmin}>
                Invite Admins
              </Menu.Item>
            )}
            {hasTokenGatedWorkspaces && (
              <Menu.Item key="gated" onClick={inviteToTokenGatedWorkspaces}>
                Invite to Token Gated Workspaces
              </Menu.Item>
            )}
          </Menu>
        }
      >
        <Button {...buttonProps}>{children ?? "Invite"}</Button>
      </Dropdown>
    );
  }

  return null;
};
